import React, { useEffect, useState } from "react";
import { CheckCircle, XCircle, AlertTriangle, Info, X } from "lucide-react";

const CONFIG = {
  success: {
    Icon: CheckCircle,
    iconCls: "text-emerald-500",
    iconBg: "bg-emerald-50",
    btnCls: "bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700",
    stripeCls: "bg-emerald-500",
    progressCls: "bg-emerald-400",
    title: "Sucesso!",
    autoClose: 4000,
  },
  error: {
    Icon: XCircle,
    iconCls: "text-rose-500",
    iconBg: "bg-rose-50",
    btnCls: "bg-rose-500 hover:bg-rose-600 active:bg-rose-700",
    stripeCls: "bg-rose-500",
    progressCls: "bg-rose-400",
    title: "Ops, algo deu errado",
    autoClose: null,
  },
  warning: {
    Icon: AlertTriangle,
    iconCls: "text-amber-500",
    iconBg: "bg-amber-50",
    btnCls: "bg-amber-500 hover:bg-amber-600 active:bg-amber-700",
    stripeCls: "bg-amber-500",
    progressCls: "bg-amber-400",
    title: "Atenção",
    autoClose: null,
  },
  info: {
    Icon: Info,
    iconCls: "text-sky-500",
    iconBg: "bg-sky-50",
    btnCls: "bg-sky-500 hover:bg-sky-600 active:bg-sky-700",
    stripeCls: "bg-sky-500",
    progressCls: "bg-sky-400",
    title: "Informação",
    autoClose: null,
  },
};

export default function NotificationModal({ show, message, type = "info", onClose }) {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  const cfg = CONFIG[type] ?? CONFIG.info;
  const { Icon, iconCls, iconBg, btnCls, stripeCls, progressCls, title, autoClose } = cfg;

  // Entrada animada com pequeno delay para o CSS ter tempo de montar
  useEffect(() => {
    if (show) {
      setProgress(100);
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [show]);

  // Auto-close com barra de progresso
  useEffect(() => {
    if (!show || !autoClose) return;

    const step = 50;
    const decrement = (step / autoClose) * 100;
    const interval = setInterval(() => {
      setProgress((prev) => {
        const next = prev - decrement;
        if (next <= 0) {
          clearInterval(interval);
          onClose();
          return 0;
        }
        return next;
      });
    }, step);

    return () => clearInterval(interval);
  }, [show, autoClose, onClose]);

  if (!show) return null;

  return (
    <div
      id="notif-overlay-background"
      data-cy="notification-overlay"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backgroundColor: `rgba(0,0,0,${visible ? 0.55 : 0})`,
        backdropFilter: visible ? "blur(4px)" : "none",
        transition: "background-color 250ms ease, backdrop-filter 250ms ease",
      }}
    >
      <div
        id="notif-modal-content"
        data-cy="notification-modal"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-2xl"
        style={{
          transform: visible ? "translateY(0) scale(1)" : "translateY(24px) scale(0.95)",
          opacity: visible ? 1 : 0,
          transition: "transform 300ms cubic-bezier(0.34,1.56,0.64,1), opacity 250ms ease",
        }}
      >
        {/* Faixa colorida no topo */}
        <div className={`h-1.5 w-full ${stripeCls}`} />

        {/* Botão fechar (canto superior direito) */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1 text-warm-400 transition-colors hover:bg-warm-100 hover:text-warm-700"
          aria-label="Fechar"
        >
          <X size={18} />
        </button>

        <div className="flex flex-col items-center px-7 pb-7 pt-8 text-center">
          {/* Ícone */}
          <div
            id="notif-icon-box"
            className={`mb-4 flex h-20 w-20 items-center justify-center rounded-full ${iconBg}`}
          >
            <Icon id="notif-lucide-icon" size={44} strokeWidth={1.5} className={iconCls} />
          </div>

          {/* Título */}
          <h3
            id="notif-title-header"
            data-cy="notification-title"
            className="font-heading mb-2 text-xl font-bold text-warm-900"
          >
            {title}
          </h3>

          {/* Mensagem */}
          <p
            id="notif-text-message"
            data-cy="notification-message"
            className="mb-6 text-sm leading-relaxed text-warm-600"
          >
            {message}
          </p>

          {/* Botão */}
          <button
            id="notif-btn-close"
            data-cy="notification-close-button"
            onClick={onClose}
            className={`w-full rounded-2xl py-3 text-sm font-bold text-white shadow-sm transition-all ${btnCls}`}
          >
            Entendido
          </button>
        </div>

        {/* Barra de progresso para auto-close */}
        {autoClose && (
          <div className="h-1 w-full bg-warm-100">
            <div
              className={`h-1 ${progressCls} transition-all`}
              style={{ width: `${progress}%`, transitionDuration: "50ms", transitionTimingFunction: "linear" }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
